import { useQuery } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { LenderFieldMap } from '@trnsact/trnsact-shared-types';
import { GetDynamicLenderMappingsResponse, GetDynamicLenderMappingsVariables } from '../api/types';
import { GET_DYNAMIC_LENDER_MAPPING } from '../api/getDynamicLenderMapping';
import { LenderType } from '@trnsact/trnsact-shared-types';
import { getStructuredMappings, prepareMappingsFromResponse } from '../lib';
import { StructuredMappings } from '../types';

interface Options {
  lenderType: LenderType;
  vendorOpportunityId: string;
  lenderProfileDynamicsId: string;
  transactionSummary: string;
  onDataLoaded?: (mappings: LenderFieldMap[]) => void;
}

export function useFetchDynamicLenderSubmissionData({
  lenderType,
  vendorOpportunityId,
  lenderProfileDynamicsId,
  transactionSummary,
  onDataLoaded,
}: Options) {
  const [dynamicLenderMappings, setDynamicLenderMappings] = useState<LenderFieldMap[]>([]);
  const onDataLoadedRef = useRef(onDataLoaded);
  onDataLoadedRef.current = onDataLoaded;

  const { data: dynamicLenderMappingsData, loading: isDynamicLenderMappingDataLoading } = useQuery<
    GetDynamicLenderMappingsResponse,
    GetDynamicLenderMappingsVariables
  >(GET_DYNAMIC_LENDER_MAPPING, {
    variables: {
      lenderType,
      vendorOpportunityId,
      lenderProfileDynamicsId,
    },
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (isDynamicLenderMappingDataLoading || !dynamicLenderMappingsData) return;

    const mappings = prepareMappingsFromResponse(
      dynamicLenderMappingsData.getMappedLenderValuesForVendorOpportunity,
      transactionSummary
    );

    setDynamicLenderMappings(mappings);

    onDataLoadedRef.current?.(mappings);
  }, [isDynamicLenderMappingDataLoading, dynamicLenderMappingsData, transactionSummary]);

  const structuredMappings = useMemo(
    () => (dynamicLenderMappings.length ? getStructuredMappings(dynamicLenderMappings) : {}),
    [dynamicLenderMappings]
  );

  return {
    isLoading: isDynamicLenderMappingDataLoading || !dynamicLenderMappings?.length,
    dynamicLenderMappings,
    structuredMappings,
    setDynamicLenderMappings,
  };
}
